import { useEffect, useMemo, useState } from "react";
import "./App.css";
import ManifestScreen from "./ManifestScreen";
import DeliveryScreen from "./DeliveryScreen";
import DeliveryOrderScreen from "./DeliveryOrderScreen";
import LoginScreen from "./LoginScreen";
import UsersScreen from "./UsersScreen";
import ReportsScreen from "./ReportsScreen";
import ServicesScreen from "./ServicesScreen";
import CargoAcceptanceScreen from "./CargoAcceptanceScreen";
import BOLIssuanceScreen from "./BOLIssuanceScreen";
import OperatorApplicationForm from "./OperatorApplicationForm";
// import BOLServicesScreen from "./BOLServicesScreen";

import { api } from "./api/api";

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

const formatMoney = (n) => {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(num);
};

function App() {
  const [activeTab, setActiveTab] = useState("bolIssuance");

  // auth state
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [permissions, setPermissions] = useState(readPerms());
  const [authMsg, setAuthMsg] = useState("");

  // ✅ NEW: Delivery Order Rates state
  const [rateMinKg, setRateMinKg] = useState("");
  const [rateMaxKg, setRateMaxKg] = useState("");
  const [ratePrice, setRatePrice] = useState("");
  const [rates, setRates] = useState([]);
  const [ratesMessage, setRatesMessage] = useState("");
  const [editingRate, setEditingRate] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ استقبل forced logout من api.js
  useEffect(() => {
    const handler = (e) => {
      setToken("");
      setPermissions([]);
      setAuthMsg(e.detail || "Logged out because another device has logged in.");
    };
    window.addEventListener("auth:forcedLogout", handler);
    return () => window.removeEventListener("auth:forcedLogout", handler);
  }, []);

  // ✅ لو عندك token جرّب /me لتأكيد الجلسة + تحديث permissions
  useEffect(() => {
    const boot = async () => {
      if (!token) return;

      try {
        const data = await api.auth.me();
        const perms = data?.user?.permissions || data?.permissions || [];
        setPermissions(perms);
        localStorage.setItem("permissions", JSON.stringify(perms));
      } catch (e) {
        api.auth.clear();
        setToken("");
        setPermissions([]);
      }
    };

    boot();
  }, [token]);

  const logout = async () => {
    try {
      if (token) await api.auth.logout();
    } catch {}
    api.auth.clear();
    setToken("");
    setPermissions([]);
    setAuthMsg("");
  };

  const allowedTabs = useMemo(() => {
    return {
      services: has(permissions, "SERVICES_EDIT") || has(permissions, "USERS_ADMIN"),
      cargoAcceptance: has(permissions, "MANIFEST_ENTRY") || has(permissions, "SERVICES_EDIT"),
      bolIssuance: has(permissions, "MANIFEST_ENTRY") || has(permissions, "SERVICES_EDIT"),
      operatorForm: has(permissions, "OPERATOR_SUBMIT") || has(permissions, "operatorForm") || has(permissions, "MANIFEST_ENTRY"),
      // bolServices: has(permissions, "SERVICES_EDIT") || has(permissions, "USERS_ADMIN"),
      manifest:
        has(permissions, "MANIFEST_ENTRY") ||
        has(permissions, "MANIFEST_CREATE") ||
        has(permissions, "MANIFEST_SEARCH") ||
        has(permissions, "MANIFEST_EDIT"),
      delivery: has(permissions, "BOL_INVOICE"),
      deliveryOrder: has(permissions, "BOL_INVOICE"),
      users: has(permissions, "USERS_ADMIN"),
      reports: has(permissions, "USERS_ADMIN"),
    };
  }, [permissions]);

  // ✅ لو التاب الحالي غير مسموح، انقله لأول تاب مسموح
  useEffect(() => {
    if (!token) return;
    const order = ["operatorForm", "bolServices", "bolIssuance", "manifest", "services", "reports", "users"];

    if (!allowedTabs[activeTab]) {
      const next = order.find((t) => allowedTabs[t]);
      setActiveTab(next || "bolIssuance");
    }
  }, [token, allowedTabs, activeTab]);

  // ===== services (كما هو عندك) =====
  useEffect(() => {
    // Services screen is now handled in ServicesScreen component
  }, [activeTab, allowedTabs.services]);

  const fetchRates = async () => {
    try {
      const res = await api.get("/delivery-order-rates");
      const list =
        Array.isArray(res) ? res :
        Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.rows) ? res.rows :
        [];
      setRates(list);
    } catch (err) {
      console.error(err);
      setRates([]);
      setRatesMessage("Error loading delivery order rates");
    }
  };

  // ✅ NEW: submit rate
  const handleSubmitRate = async (e) => {
    e.preventDefault();
    setRatesMessage("");

    const min = Number(rateMinKg);
    const max = Number(rateMaxKg);
    const p = Number(ratePrice);

    if (!rateMinKg || !rateMaxKg || !ratePrice) {
      setRatesMessage("Please enter min KG, max KG and price");
      return;
    }
    if (Number.isNaN(min) || Number.isNaN(max) || Number.isNaN(p)) {
      setRatesMessage("Invalid numbers");
      return;
    }
    if (min <= 0 || max <= 0 || p < 0) {
      setRatesMessage("KG must be > 0 and price must be >= 0");
      return;
    }
    if (min > max) {
      setRatesMessage("Min KG must be <= Max KG");
      return;
    }

    try {
      setLoading(true);

if (editingRate) {
  await api.put(`/delivery-order-rates/${editingRate.id}`, {
    minKg: min,
    maxKg: max,
    deliveryPrice: p,
    // isActive: 1, // اختياري
  });
  setRatesMessage("Rate updated successfully ✅");
} else {
  await api.post("/delivery-order-rates", {
    minKg: min,
    maxKg: max,
    deliveryPrice: p,
    // isActive: 1, // اختياري
  });
  setRatesMessage("Rate added successfully ✅");
}


      setRateMinKg("");
      setRateMaxKg("");
      setRatePrice("");
      setEditingRate(null);
      fetchRates();
    } catch (err) {
      console.error(err);
      setRatesMessage(err.message || "Server connection error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: delete rate
  const handleDeleteRate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rate?")) return;
    try {
      setLoading(true);
      await api.del(`/delivery-order-rates/${id}`);
      fetchRates();
    } catch (err) {
      console.error(err);
      setRatesMessage(err.message || "Error deleting rate");
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: edit rate
  const handleEditRate = (r) => {
    setRateMinKg(String(r.min_kg ?? r.minKg ?? ""));
    setRateMaxKg(String(r.max_kg ?? r.maxKg ?? ""));
    setRatePrice(String(r.delivery_price ?? ""));

    setEditingRate(r);
  };

  // ✅ لو غير مسجل دخول: اعرض Login
  if (!token) {
    return (
      <div className="App">
        <header className="App-header">
          {authMsg && <div className="message error">{authMsg}</div>}
          <LoginScreen
            onLoggedIn={(data) => {
              setToken(data.token);
              setPermissions(data.permissions || []);
              setAuthMsg("");
              const first = ["operatorForm", "bolIssuance", "manifest", "services", "reports", "users"]
                .find((t) => allowedTabs[t]);
              setActiveTab(first || "bolIssuance");
            }}
          />
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
          <div className="tabs">
            {allowedTabs.services && (
              <button onClick={() => setActiveTab("services")} className={activeTab === "services" ? "active-tab" : ""}>
                Services
              </button>
            )}

            {/* {allowedTabs.cargoAcceptance && (
              <button onClick={() => setActiveTab("cargoAcceptance")} className={activeTab === "cargoAcceptance" ? "active-tab" : ""}>
                Cargo Acceptance
              </button>
            )} */}

            {allowedTabs.operatorForm && (
              <button onClick={() => setActiveTab("operatorForm")} className={activeTab === "operatorForm" ? "active-tab" : ""}>
                Operator Submission
              </button>
            )}

            {allowedTabs.bolIssuance && (
              <button onClick={() => setActiveTab("bolIssuance")} className={activeTab === "bolIssuance" ? "active-tab" : ""}>
                AWB Issuance
              </button>
            )}

            {/* {allowedTabs.bolServices && (
              <button onClick={() => setActiveTab("bolServices")} className={activeTab === "bolServices" ? "active-tab" : ""}>
                BOL Services
              </button>
            )} */}

            {allowedTabs.manifest && (
              <button onClick={() => setActiveTab("manifest")} className={activeTab === "manifest" ? "active-tab" : ""}>
                Manifest
              </button>
            )}

            {/* {allowedTabs.delivery && (
              <button onClick={() => setActiveTab("delivery")} className={activeTab === "delivery" ? "active-tab" : ""}>
                Delivery
              </button>
            )}

            {allowedTabs.deliveryOrder && (
              <button onClick={() => setActiveTab("deliveryOrder")} className={activeTab === "deliveryOrder" ? "active-tab" : ""}>
                Delivery Order
              </button>
            )} */}

            {allowedTabs.reports && (
  <button
    onClick={() => setActiveTab("reports")}
    className={activeTab === "reports" ? "active-tab" : ""}
  >
    Reports
  </button>
)}


            {allowedTabs.users && (
              <button onClick={() => setActiveTab("users")} className={activeTab === "users" ? "active-tab" : ""}>
                Users
              </button>
            )}
          </div>

          <button type="button" className="secondary-btn" onClick={logout}>
            Logout
          </button>
        </div>

        {token && permissions.length > 0 && !Object.values(allowedTabs).some(Boolean) && (
          <div className="message error" style={{ marginTop: "1rem" }}>
            لا توجد صلاحيات مخصصة لهذا المستخدم. تواصل مع المدير لإضافة الصلاحيات.
          </div>
        )}

        {/* Content */}
        {activeTab === "services" && allowedTabs.services && (
          <ServicesScreen />
        )}

        {activeTab === "cargoAcceptance" && allowedTabs.cargoAcceptance && (
          <CargoAcceptanceScreen />
        )}

        {activeTab === "operatorForm" && allowedTabs.operatorForm && (
          <OperatorApplicationForm />
        )}

        {activeTab === "bolIssuance" && allowedTabs.bolIssuance && (
          <BOLIssuanceScreen />
        )}

        {/* {activeTab === "bolServices" && allowedTabs.bolServices && (
          <BOLServicesScreen />
        )} */}

        {activeTab === "manifest" && allowedTabs.manifest && (
          <ManifestScreen permissions={permissions} />
        )}
        {activeTab === "delivery" && allowedTabs.delivery && <DeliveryScreen />}
        {activeTab === "deliveryOrder" && allowedTabs.deliveryOrder && <DeliveryOrderScreen />}
        {activeTab === "users" && allowedTabs.users && <UsersScreen permissions={permissions} />}
        {activeTab === "reports" && allowedTabs.reports && <ReportsScreen />}

      </header>
    </div>
  );
}

export default App;
